import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment,
  Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, useMediaQuery, useTheme,
} from "@material-ui/core";
import {
  Add, Clear, DeleteOutline, DescriptionOutlined, EditOutlined, FileCopyOutlined,
  InfoOutlined, Refresh, Search, VisibilityOutlined,
} from "@material-ui/icons";
import { Alert, Pagination, Skeleton } from "@material-ui/lab";
import axios from "services/auth/jwt/config";
import { errorText } from "routes/Pages/FormTickets/utils";
import { getFormPreviewUrl } from "../FormPreviewButton";
import useStyles from "../recordListStyles";
import FormElemnt from "./FormElemnt";

export default function FormsContainer() {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [result, setResult] = useState({ docs: [], totalDocs: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actionError, setActionError] = useState("");
  const [menu, setMenu] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios.get("/form/getall", { params: { page, limit: 10, searchString: search } })
      .then(({ data }) => {
        if (!active) return;
        setResult(data);
        if (page > Math.max(1, data.totalPages)) setPage(Math.max(1, data.totalPages));
      })
      .catch(err => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, search, revision]);

  const copyForm = async form => {
    setMenu(null);
    setBusy(true);
    setSuccess("");
    setActionError("");
    const copy = { ...form, title: `${form.title} کپی`, active: false,
      fields: (form.fields || []).map(field => {
        const value = { ...field };
        delete value._id;
        return value;
      }) };
    ["_id", "updatedAt", "createdAt", "__v"].forEach(key => { delete copy[key]; });
    try {
      await axios.post("/form/create", copy);
      setSuccess("کپی فرم با موفقیت ایجاد شد و غیرفعال است");
      setPage(1);
      setRevision(value => value + 1);
    } catch (err) { setActionError(errorText(err)); }
    finally { setBusy(false); }
  };
  const removeForm = async () => {
    setBusy(true);
    setDeleteError("");
    setSuccess("");
    setActionError("");
    try {
      await axios.delete(`/form/remove/${deleting._id}`);
      setDeleting(null);
      setSuccess("فرم با موفقیت حذف شد");
      setRevision(value => value + 1);
    } catch (err) { setDeleteError(errorText(err)); }
    finally { setBusy(false); }
  };
  const previewUrl = menu ? getFormPreviewUrl(menu.form._id) : undefined;
  const firstRecord = result.docs.length ? (page - 1) * 10 + 1 : 0;
  const lastRecord = (page - 1) * 10 + result.docs.length;

  return <>
    <Box className={classes.hero}>
      <Box className={classes.heroIdentity}>
        <Box className={classes.heroIcon}><DescriptionOutlined /></Box>
        <Box><Typography className={classes.eyebrow}>ساخت فرم، مدیریت آزمون‌ها و پیگیری پاسخ‌ها</Typography>
          <Typography component="h2" className={classes.heroTitle}>مدیریت فرم‌ها</Typography></Box>
      </Box>
      <Box className={classes.heroActions}>
        <Button component={Link} to="/formBuilder" variant="contained" color="primary" startIcon={<Add />} className={classes.button}>ساخت فرم جدید</Button>
        <Button onClick={() => setRevision(value => value + 1)} disabled={loading || busy} startIcon={<Refresh />} className={classes.button}>بارگذاری مجدد</Button>
      </Box>
    </Box>
    <Paper elevation={0} className={classes.panel}>
      <Box className={classes.panelHeader}>
        <Typography component="h3" className={classes.panelTitle}>فهرست فرم‌ها</Typography>
        {!loading && !error && <Box component="span" className={classes.count} px={1.5} py={0.5} borderRadius={8} fontSize={12}>
          {result.totalDocs.toLocaleString("fa-IR")} {search ? "نتیجه" : "فرم"}
        </Box>}
      </Box>
      <Box component="form" className={classes.filters} onSubmit={event => { event.preventDefault(); setPage(1); setSearch(searchDraft.trim()); }}>
        <TextField id="forms-search" label="جستجوی فرم‌ها" placeholder="عنوان فرم یا آزمون را وارد کنید" variant="outlined" size="small"
          value={searchDraft} onChange={event => setSearchDraft(event.target.value)} inputProps={{ maxLength: 200 }} className={classes.search}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search className={classes.searchIcon} /></InputAdornment> }} />
        <Button type="submit" variant="contained" color="primary" disabled={loading || busy} startIcon={<Search />} className={classes.button}>جستجو</Button>
        {search && <Button onClick={() => { setSearchDraft(""); setSearch(""); setPage(1); }} disabled={loading || busy} startIcon={<Clear />} className={classes.button}>پاک کردن جستجو</Button>}
      </Box>
      {success && <Alert severity="success" className={classes.alert} onClose={() => setSuccess("")}>{success}</Alert>}
      {actionError && <Alert severity="error" className={classes.alert} onClose={() => setActionError("")}>{actionError}</Alert>}
      {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
      {loading ? <Box className={classes.loading}>
        <Typography role="status" color="textSecondary">در حال دریافت فرم‌ها…</Typography>
        {[1, 2, 3].map(value => <Skeleton key={value} variant="rect" height={86} aria-hidden="true" />)}
      </Box> : !error && <>
        {!result.docs.length ? <Box className={classes.empty}>
          {search ? <Search /> : <DescriptionOutlined />}
          <Typography className={classes.emptyTitle}>{search ? "فرمی یافت نشد" : "هنوز فرمی ساخته نشده است"}</Typography>
          <Typography color="textSecondary" variant="body2">{search ? "عنوان دیگری را جستجو کنید یا جستجو را پاک کنید." : "برای شروع، یک فرم ثبت‌نام یا آزمون جدید بسازید."}</Typography>
          {!search && <Box mt={2}><Button component={Link} to="/formBuilder" color="primary" variant="outlined" startIcon={<Add />} className={classes.button}>ساخت اولین فرم</Button></Box>}
        </Box> : mobile ? <Box className={classes.mobileList}>
          {result.docs.map(form => <FormElemnt key={form._id} data={form} mobile busy={busy} onMenu={(event, target) => setMenu({ anchor: event.currentTarget, form: target })} />)}
        </Box> : <TableContainer><Table aria-label="فهرست فرم‌ها" className={classes.table}>
          <TableHead><TableRow>{["فرم", "وضعیت", "مشخصات", "تاریخ ایجاد", "عملیات"].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{result.docs.map(form => <FormElemnt key={form._id} data={form} busy={busy} onMenu={(event, target) => setMenu({ anchor: event.currentTarget, form: target })} />)}</TableBody>
        </Table></TableContainer>}
        {result.totalDocs > 0 && <Box className={classes.footer}>
          <Typography variant="caption" color="textSecondary">نمایش {firstRecord.toLocaleString("fa-IR")}–{lastRecord.toLocaleString("fa-IR")} از {result.totalDocs.toLocaleString("fa-IR")} فرم</Typography>
          {result.totalPages > 1 && <Pagination count={result.totalPages} page={page} color="primary" shape="rounded" disabled={busy} onChange={(_, value) => setPage(value)} />}
        </Box>}
      </>}
    </Paper>
    <Box className={classes.help}><InfoOutlined /><Typography>پاسخ‌های نهایی از بخش ورودی‌ها و پاسخ‌های در حال ثبت آزمون از بخش مدیریت بلیط‌ها در دسترس هستند.</Typography></Box>
    <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}
      getContentAnchorEl={null} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} classes={{ paper: classes.menuPaper }}>
      {menu && <MenuItem component={Link} to={`/formBuilder/${menu.form._id}`} className={classes.menuItem}><EditOutlined />ویرایش فرم</MenuItem>}
      <MenuItem component="a" href={previewUrl} target="_blank" rel="noopener noreferrer" disabled={!previewUrl} className={classes.menuItem} onClick={() => setMenu(null)}><VisibilityOutlined />پیش‌نمایش</MenuItem>
      {menu && <MenuItem className={classes.menuItem} onClick={() => copyForm(menu.form)}><FileCopyOutlined />کپی فرم</MenuItem>}
      {menu && <MenuItem className={`${classes.menuItem} ${classes.deleteAction}`} onClick={() => { setDeleting(menu.form); setDeleteError(""); setMenu(null); }}><DeleteOutline />حذف فرم</MenuItem>}
    </Menu>
    <Dialog open={Boolean(deleting)} onClose={() => { if (!busy) setDeleting(null); }} fullWidth maxWidth="sm"
      aria-labelledby="delete-form-title" PaperProps={{ className: classes.dialogPaper }}>
      <DialogTitle id="delete-form-title" className={classes.dialogTitle}>حذف فرم</DialogTitle>
      <DialogContent>
        <Box className={classes.dialogParticipant}><DescriptionOutlined color="primary" /><Typography className={classes.participantName}>{deleting?.title}</Typography></Box>
        <Alert severity="warning">آیا از حذف این فرم اطمینان دارید؟ فرم پس از حذف از فهرست فرم‌ها خارج می‌شود.</Alert>
        {deleteError && <Box mt={2}><Alert severity="error">{deleteError}</Alert></Box>}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button disabled={busy} onClick={() => setDeleting(null)} className={classes.button}>انصراف</Button>
        <Button disabled={busy} onClick={removeForm} variant="contained" color="secondary" className={classes.button}>{busy ? "در حال حذف…" : "تایید حذف"}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
